import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Snackbar,
    Alert,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { API_URL, apiFetch } from "../config";
import GlowCard from "../components/ui/GlowCard";

const emptyForm = { title: "", body: "", customer_id: "" };

function formatCreated(iso) {
    if (!iso) return "—";
    const value = String(iso).includes("T") ? iso : String(iso).replace(" ", "T");
    const t = Date.parse(value);
    if (!Number.isFinite(t)) return String(iso).slice(0, 16);
    return new Date(t).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export default function Notes() {
    const [notes, setNotes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState("");
    const [customerFilter, setCustomerFilter] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const load = useCallback(async () => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        if (customerFilter?.id) params.set("customer_id", customerFilter.id);
        const query = params.toString();
        const res = await apiFetch(`${API_URL}/notes${query ? `?${query}` : ""}`);
        if (!res.ok) throw new Error("Unable to load notes.");
        setNotes(await res.json());
    }, [search, customerFilter]);

    const loadCustomers = useCallback(async () => {
        const res = await apiFetch(`${API_URL}/customers`);
        if (!res.ok) return;
        const data = await res.json();
        setCustomers(Array.isArray(data) ? data : data.customers || []);
    }, []);

    useEffect(() => {
        load().catch((error) =>
            setSnackbar({ open: true, message: error.message || "Unable to load notes.", severity: "error" })
        );
    }, [load]);

    useEffect(() => {
        loadCustomers().catch(() => { });
    }, [loadCustomers]);

    const selectedCustomer = useMemo(
        () => customers.find((c) => c.id === form.customer_id) || null,
        [customers, form.customer_id]
    );

    function openCreate() {
        setEditing(null);
        setForm(emptyForm);
        setDialogOpen(true);
    }

    function openEdit(note) {
        setEditing(note);
        setForm({
            title: note.title || "",
            body: note.body || "",
            customer_id: note.customer_id || "",
        });
        setDialogOpen(true);
    }

    async function save() {
        const body = form.body.trim();
        if (!body) {
            setSnackbar({ open: true, message: "Write a note first.", severity: "error" });
            return;
        }
        setBusy(true);
        try {
            const payload = {
                title: form.title.trim(),
                body,
                customer_id: form.customer_id || null,
            };
            const res = await apiFetch(editing ? `${API_URL}/notes/${editing.id}` : `${API_URL}/notes`, {
                method: editing ? "PUT" : "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.error || "Unable to save note.");
            }
            setDialogOpen(false);
            setSnackbar({ open: true, message: editing ? "Note updated." : "Note added.", severity: "success" });
            await load();
        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: "error" });
        } finally {
            setBusy(false);
        }
    }

    async function remove(note) {
        if (!window.confirm(`Delete this note${note.title ? ` (“${note.title}”)` : ""}?`)) return;
        setBusy(true);
        try {
            const res = await apiFetch(`${API_URL}/notes/${note.id}`, { method: "DELETE" });
            if (!res.ok && res.status !== 204) throw new Error("Unable to delete note.");
            setSnackbar({ open: true, message: "Note deleted.", severity: "success" });
            await load();
        } catch (error) {
            setSnackbar({ open: true, message: error.message, severity: "error" });
        } finally {
            setBusy(false);
        }
    }

    return (
        <Stack spacing={2.5}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ sm: "flex-end" }}
                spacing={1.5}
            >
                <Stack spacing={0.5}>
                    <Typography variant="overline" color="primary.main" sx={{ letterSpacing: ".16em", fontWeight: 800 }}>
                        Pipeline
                    </Typography>
                    <Typography variant="h4" sx={{ fontSize: { xs: "1.65rem", sm: "2.125rem" } }}>
                        Notes
                    </Typography>
                    <Typography color="text.secondary" sx={{ display: { xs: "none", sm: "block" } }}>
                        Shop notes on a board. Tag a customer when the note is about them.
                    </Typography>
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    New note
                </Button>
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                <TextField
                    size="small"
                    label="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    fullWidth
                    sx={{ maxWidth: { sm: 320 } }}
                />
                <Autocomplete
                    size="small"
                    options={customers}
                    getOptionLabel={(option) => option.name || ""}
                    value={customerFilter}
                    onChange={(_e, value) => setCustomerFilter(value)}
                    sx={{ minWidth: { sm: 260 }, flex: 1, maxWidth: { sm: 360 } }}
                    renderInput={(params) => <TextField {...params} label="Customer" />}
                />
            </Stack>

            {!notes.length ? (
                <GlowCard>
                    <Stack spacing={1} alignItems="flex-start">
                        <Typography color="text.secondary">No notes yet.</Typography>
                        <Button startIcon={<AddIcon />} onClick={openCreate}>
                            Add the first note
                        </Button>
                    </Stack>
                </GlowCard>
            ) : (
                <Grid container spacing={1.75}>
                    {notes.map((note) => (
                        <Grid key={note.id} size={{ xs: 12, sm: 6, md: 4 }}>
                            <GlowCard
                                hover
                                icon={StickyNote2Icon}
                                title={note.title || "Untitled note"}
                                subtitle={formatCreated(note.created_at)}
                                action={
                                    <Stack direction="row">
                                        <IconButton size="small" aria-label="Edit note" onClick={() => openEdit(note)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            aria-label="Delete note"
                                            onClick={() => remove(note)}
                                            disabled={busy}
                                        >
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                }
                                sx={{ height: "100%" }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        whiteSpace: "pre-wrap",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 8,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        minHeight: 48,
                                    }}
                                >
                                    {note.body}
                                </Typography>
                                <Box sx={{ mt: 1.5 }}>
                                    {note.customer_name ? (
                                        <Chip size="small" label={note.customer_name} color="primary" variant="outlined" />
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            No customer
                                        </Typography>
                                    )}
                                </Box>
                            </GlowCard>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{editing ? "Edit note" : "New note"}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Title"
                        fullWidth
                        margin="normal"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                    />
                    <TextField
                        label="Note"
                        required
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={5}
                        value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                    />
                    <Autocomplete
                        options={customers}
                        getOptionLabel={(option) => option.name || ""}
                        value={selectedCustomer}
                        onChange={(_e, value) => setForm({ ...form, customer_id: value?.id || "" })}
                        renderInput={(params) => (
                            <TextField {...params} label="Tag a customer" margin="normal" helperText="Optional" />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={save} disabled={busy || !form.body.trim()}>
                        {editing ? "Save" : "Add note"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Stack>
    );
}
